import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Star, ArrowRight, Flame } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";

const WestMemphis = () => (
  <PageLayout testId="west-memphis-page">
    {/* Hero */}
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/assets/photos/IMG_3431.jpeg')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-5 flex items-center gap-2 font-bold">
          <Link to="/" className="hover:text-[#E60000]">Home</Link><span>/</span>
          <Link to="/visit" className="hover:text-[#E60000]">Visit</Link><span>/</span>
          <span className="text-[#E60000]">West Memphis</span>
        </nav>
        <div className="inline-flex items-center gap-2 bg-[#E60000] text-white px-3 py-1.5 mb-6 text-xs uppercase tracking-widest font-bold">
          <Star className="w-3 h-3" strokeWidth={3} /> Now Open · Summer 2025
        </div>
        <h1 className="font-heading text-6xl sm:text-7xl md:text-8xl text-white leading-none mb-4">
          West Memphis <br /><span className="text-[#E60000]">Arkansas</span>
        </h1>
        <p className="text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed mb-8">
          Crossed the bridge. Same wings, same flavor, same family. Our newest Hillboyz spot
          is now serving 11 years of Memphis heat from a brand-new West Memphis address.
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            data-testid="wm-directions-btn"
            href="https://maps.google.com/?daddr=109+S+Worthington+Drive+West+Memphis+AR+72301"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-6 h-12 uppercase tracking-widest text-sm font-bold border-2 border-transparent hover:border-white transition-colors"
          >
            <MapPin className="w-4 h-4" strokeWidth={3} /> Get Directions
          </a>
          <a
            href="tel:8703944229"
            data-testid="wm-call-btn"
            className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-[#333] hover:border-[#E60000] px-6 h-12 uppercase tracking-widest text-sm font-bold transition-colors"
          >
            <Phone className="w-4 h-4" strokeWidth={3} /> Call
          </a>
          <Link
            to="/menu"
            data-testid="wm-menu-btn"
            className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-[#333] hover:border-[#E60000] px-6 h-12 uppercase tracking-widest text-sm font-bold transition-colors"
          >
            <Flame className="w-4 h-4" strokeWidth={3} /> See Menu
          </Link>
        </div>
      </div>
    </section>

    {/* Info cards */}
    <section className="bg-[#0A0A0A] py-16 md:py-20 border-t border-[#222]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-5">
        <Card icon={MapPin} title="Address" lines={["109 S Worthington Drive", "West Memphis, AR 72301"]} />
        <Card icon={Phone} title="Call Ahead" lines={["(870) 394-4229"]} />
        <Card
          icon={Clock}
          title="Hours"
          lines={["Mon – Fri  10A – 10P", "Saturday  12P – 10P", "Sunday     Closed"]}
        />
      </div>
    </section>

    {/* Map */}
    <section className="bg-[#0A0A0A] pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="aspect-[16/8] border-2 border-[#333] overflow-hidden relative">
          <iframe
            title="West Memphis location map"
            src="https://www.google.com/maps?q=109+S+Worthington+Drive+West+Memphis+AR+72301&output=embed"
            className="w-full h-full grayscale-[80%] contrast-125"
            loading="lazy"
            allowFullScreen
          />
          <div className="absolute top-4 left-4 bg-[#E60000] text-white px-3 py-2 text-xs uppercase tracking-widest font-bold">
            Hillboyz · West Memphis
          </div>
        </div>
      </div>
    </section>

    {/* What's there */}
    <section className="bg-[#1A1A1A] py-16 md:py-20 border-t border-[#222]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold mb-3">Same Menu · Same Squad</div>
        <h2 className="font-heading text-4xl sm:text-5xl text-white mb-4">
          The Full Hillboyz <span className="text-[#E60000]">Experience</span>
        </h2>
        <p className="text-white/70 max-w-2xl mx-auto mb-8">
          31 wing flavors, 12 burger builds, 6 hand-crafted Phillys, "On The Bluff" seafood, loaded nachos,
          creamy pastas, banana pudding made fresh daily — all of it, on the Arkansas side too.
        </p>
        <Link
          to="/menu"
          className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-6 h-12 uppercase tracking-widest text-sm font-bold border-2 border-transparent hover:border-white transition-colors"
        >
          Browse The Menu <ArrowRight className="w-4 h-4" strokeWidth={3} />
        </Link>
      </div>
    </section>
  </PageLayout>
);

const Card = ({ icon: Icon, title, lines }) => (
  <div className="border border-[#333] bg-[#1A1A1A] p-6 hover:border-[#E60000] transition-colors">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-[#E60000] flex items-center justify-center">
        <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
      </div>
      <h3 className="font-heading text-2xl text-white">{title}</h3>
    </div>
    <div className="text-sm text-white/80 font-mono leading-relaxed space-y-0.5">
      {lines.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  </div>
);

export default WestMemphis;
